import { addDays, getUnixTime } from 'date-fns';
import { CONFIG } from '@frameio/config';
import { sleep } from '../../async/sleep';
import { isDesktop, isMainThread } from '../../config/helpers';
import { getCookie, removeCookie, setCookie } from '../../cookies';
import { online } from '../../events/online';
import { normalizeSlashes, sanitizeUrl } from '../../formatters';
import { recursiveDecodeURIComponent } from '../../formatters/recursiveDecodeURIComponent';
import { session } from '../../session';
import { CREDENTIAL_KEYS } from '../constants';
import { fetchNewAccessToken, TransientError } from '../fetchNewAccessToken';
import { isValidAccessToken } from '../isValidAccessToken';
import { removeBearerPrefix } from '../tokenPrefix';
import type { AccessTokens, AuthState, FetchedAccessTokens, Hash } from '.';
import { Auth, type HandleAuthCallbackOpts } from './baseAuth';

const {
  CALLBACK_ACCESS_TOKEN,
  CALLBACK_SESSION_TOKEN,
  CALLBACK_REFRESH_TOKEN,
  CALLBACK_USER_ID,
} = CREDENTIAL_KEYS;

// @todo (SHU-8346): [FE] Inbox - Auth: Use generated type for MeForInboxRedirectResponse
type MeForInboxRedirectResponse = {
  me?: {
    id: string;
    __typename: string;
    accounts?: {
      nodes: Array<{
        account: {
          id: string;
        };
      }>;
    };
    inboxAccounts?: {
      nodes: Array<{
        id: string;
      }>;
    };
  };
};

export class WindowAuth extends Auth {
  /**
   * Get the access token from the user's cookies.
   *
   * @environments client
   * @returns the access token if the user is authenticated
   */
  getAccessToken(): string | undefined {
    /**
     * Patch for WEB-1829, where we are removing the "Bearer " prefix from the
     * access token in the cookie. Existing sessions will have the "Bearer "
     * prefix until they refresh, so we need to remove it.
     */
    const accessToken = removeBearerPrefix(
      getCookie(CONFIG.auth.accessTokenKey) as string | undefined | null
    );
    if (!accessToken) return undefined;

    return accessToken;
  }

  /**
   * Get the refresh token from the user's cookies. On the client side, this is
   * cached on an instance variable. On the server, we always get the cookie value.
   *
   * @environments client
   * @param [serverSideCookieHeader] On the server, pass in the cookie string from the request header
   *  for use when instantiating universal-cookie
   * @returns the refresh token if the user is authenticated
   */
  getRefreshToken(): string | undefined {
    return getCookie(CONFIG.auth.refreshTokenKey);
  }

  /**
   * Get the user session token from the user's cookies.
   *
   * @environments client
   * @returns the user session token if the user is authenticated
   */
  getSessionToken(): string | undefined {
    return getCookie(CONFIG.auth.sessionTokenKey);
  }

  // we don't have a cookie for the share token
  #shareToken: string | undefined = undefined;

  /**
   * Get the share token for the current runtime environment
   *
   * @environments client
   * @returns the share token if it exists
   */
  getShareToken(): string | undefined {
    return this.#shareToken;
  }

  /**
   * Set the JWT access token cookie and instance variable.
   *
   * @environments client
   * @param [accessToken] the access token to set
   */
  setAccessToken(accessToken: string): void {
    const now = new Date();
    const expires = addDays(now, CONFIG.auth.tokenTtlDays);

    // Clean up stale host-scoped cookies left over from before the
    // useCurrentHostAsCookieDomain removal.
    removeCookie(CONFIG.auth.accessTokenKey, {
      domain: window.location.hostname,
    });

    const cookieNeedsUpdating =
      getCookie(CONFIG.auth.accessTokenKey) !== accessToken;

    if (cookieNeedsUpdating) {
      const value = removeBearerPrefix(accessToken);
      setCookie(CONFIG.auth.accessTokenKey, value, {
        // use the expiration from the JWT for the cookie
        expires,
      });
    }
  }

  /**
   * Set the refresh token cookie and instance variable.
   *
   * @environments client
   * @param [refreshToken] the refresh token to set
   */
  setRefreshToken(refreshToken?: string): void {
    // Clean up stale host-scoped cookies left over from before the
    // useCurrentHostAsCookieDomain removal.
    removeCookie(CONFIG.auth.refreshTokenKey, {
      domain: window.location.hostname,
    });

    const cookieNeedsUpdating =
      refreshToken && getCookie(CONFIG.auth.refreshTokenKey) !== refreshToken;

    if (cookieNeedsUpdating) {
      const now = new Date();
      const expires = addDays(now, CONFIG.auth.tokenTtlDays);

      setCookie(CONFIG.auth.refreshTokenKey, refreshToken, {
        expires,
      });

      // @todo (GROW-3361): remove cookie once data is collected for BUGS-2197
      setCookie(
        CONFIG.auth.refreshTokenStartTimeKey,
        `${getUnixTime(new Date())}`,
        {
          expires,
        }
      );
    }
  }

  /**
   * Set the session token instance variable.
   *
   * @environments client
   * @param [sessionToken] the session token to set
   */
  setSessionToken(sessionToken: string): void {
    const now = new Date();
    const expires = addDays(now, CONFIG.auth.tokenTtlDays);

    setCookie(CONFIG.auth.sessionTokenKey, sessionToken, {
      expires,
    });
  }

  setShareToken(shareToken: string) {
    this.#shareToken = shareToken;
  }

  removeRefreshToken() {
    delCookie(CONFIG.auth.refreshTokenKey);
    delCookie(CONFIG.auth.refreshTokenStartTimeKey);
  }

  removeAccessToken() {
    delCookie(CONFIG.auth.accessTokenKey);
  }

  removeSessionToken() {
    delCookie(CONFIG.auth.sessionTokenKey);
  }

  removeShareToken() {
    this.#shareToken = undefined;
  }

  /**
   * On state changes, update the integration core API credentials.
   */
  override setState(state: AuthState) {
    super.setState(state);
    if (isDesktop() && state.tokens.access) {
      window.integrationCoreApi?.setCredentials({
        token: state.tokens.access,
        refreshToken: state.tokens.refresh,
        sessionToken: state.tokens.session,
      });
    }
  }

  #interval: NodeJS.Timeout | undefined = undefined;

  protected addSubscriber(subscriber: (state: AuthState) => void): void {
    super.addSubscriber(subscriber);
    const cs = getCookieStore();
    if (cs) {
      cs.addEventListener('change', this.state);
    } else if (!this.#interval) {
      this.#interval = setInterval(this.state, 1000);
    }
  }

  protected removeSubscriber(subscriber: (state: AuthState) => void): void {
    super.removeSubscriber(subscriber);
    if (this.subscribers.size === 0) {
      const cs = getCookieStore();
      if (cs) {
        cs.removeEventListener('change', this.state);
      } else if (this.#interval) {
        clearInterval(this.#interval);
        this.#interval = undefined;
      }
    }
  }

  /**
   * Refresh the auth state, requesting new tokens if needed.
   *
   * To avoid a race condition, refresh is synchronized across
   * tabs using a navigator lock. If {state} is provided, refresh
   * will return without refreshing if the state has updated since
   * it was called.
   *
   * @param state - the current state or hash
   * @returns the new auth state
   */
  override async handleRefresh(
    state: Hash<AccessTokens>,
    options: { signal?: AbortSignal }
  ): Promise<AuthState> {
    const { signal } = options;
    return navigator.locks.request(
      'REFRESH_ACCESS_TOKEN_WEB_LOCK',
      {
        mode: 'exclusive',
        signal: options.signal,
      },
      async (__lock__) => {
        const current = await this.state();
        if (current.hash !== state) {
          return current;
        }

        await online(signal);
        signal?.throwIfAborted();
        this.setTokens(
          await this.fetchAccessToken(
            this.getRefreshToken(),
            this.getSessionToken()
          )
        );
        return this.state();
      }
    );
  }

  #inFlightCallback?: Promise<string>;

  /**
   * Upon authentication, the auth portal will redirect the user to a callback
   * endpoint with the auth credentials affixed as query parameters. This fn
   * takes that full URL as a string, extracts those credentials, and stores
   * them (cookies & localStorage) for retrieval later when making API calls
   * that require authentication.
   *
   * Invocations are deduped for the lifetime of the page load: duplicate or
   * concurrent calls share one in-flight promise so the credential handling,
   * MeForInboxRedirect query, and redirect only run once.
   *
   * @environments client
   * @param authCallbackUrlString - The auth callback URL that the auth portal has
   *   redirected to. In most cases, the user is currently on this URL.
   * @param options - Configuration options. At the moment, the only option is
   * "skipInboxRedirect", which skips the inbox-only account check and redirect.
   * Defaults to false.
   * @returns The web app URL to redirect (again) the user to after we have
   *   obtained the creds
   */
  public handleAuthCallback = (
    authCallbackUrlString: string,
    options?: HandleAuthCallbackOpts
  ): Promise<string> => {
    this.#inFlightCallback ??= this.#resolveAuthCallback(
      authCallbackUrlString,
      options
    );
    return this.#inFlightCallback;
  };

  #resolveAuthCallback = async (
    authCallbackUrlString: string,
    options?: HandleAuthCallbackOpts
  ): Promise<string> => {
    const authCallbackUrl = new URL(authCallbackUrlString);

    // creds are sent in a querystring-style hash
    // obtain them from the query string
    const credentials = new URLSearchParams(
      authCallbackUrl?.hash?.substring(1)
    );
    const accessToken = credentials.get(CALLBACK_ACCESS_TOKEN) || undefined;
    const sessionToken = credentials.get(CALLBACK_SESSION_TOKEN) || undefined;
    const refreshToken = credentials.get(CALLBACK_REFRESH_TOKEN) || undefined;
    const userId = credentials.get(CALLBACK_USER_ID) || undefined;

    // store cookies for the current hostname/environment
    if (accessToken) {
      this.setAccessToken(accessToken);
    }
    if (refreshToken) {
      this.setRefreshToken(refreshToken);
    }
    if (sessionToken) {
      this.setSessionToken(sessionToken);
    } else {
      this.removeSessionToken();
    }
    if (userId) {
      this.setUserId(userId);
    }

    if (!options?.skipInboxRedirect) {
      // Get Inbox accountId for redirect
      const response = await this.query<MeForInboxRedirectResponse, never>({
        query: `query MeForInboxRedirect {
          me {
            id
            __typename
            ... on AuthenticatedUser {
              id
              accounts(page: { first: 1 }) {
                nodes {
                  account {
                    id
                  }
                }
              }
              inboxAccounts(page: { first: 1 }) {
                nodes {
                  id
                }
              }
            }
          }
        }`,
        operationName: 'MeForInboxRedirect',
      });

      // If user has access to only Inbox Accounts, redirect to Inbox
      if (response?.data?.me?.__typename === 'AuthenticatedUser') {
        const inboxAccountId = response?.data?.me?.inboxAccounts?.nodes[0]?.id;

        if (!response?.data?.me?.accounts?.nodes?.length && inboxAccountId) {
          return `/inbox/${inboxAccountId}`;
        }
      }
    }

    // create a URL from the redirect path
    let redirectPath = authCallbackUrl.searchParams.get('redirect_path') || '';

    // prevent double decoded XSS attacks. See: https://frame-io.atlassian.net/browse/WEB-1555
    try {
      redirectPath = recursiveDecodeURIComponent(redirectPath);
    } catch {
      // do nothing
    }

    // Prevent path traversal
    redirectPath.replace('..', '');

    // prevent malicious characters for an XSS Attack. See: https://frame-io.atlassian.net/browse/WEB-1396
    redirectPath = sanitizeUrl(redirectPath);

    // Prevent redirects to other domains
    redirectPath = normalizeSlashes(redirectPath);

    return redirectPath;
  };

  /**
   * Fetch the refreshed access token from the backend
   *
   * @environments client
   * @returns the fetch Promise
   */
  protected override async fetchAccessToken(
    refreshToken?: string,
    sessionToken?: string
  ): Promise<FetchedAccessTokens> {
    if ((!refreshToken || refreshToken === 'cleared') && !sessionToken) {
      throw new Error('No refresh or session token is available');
    }

    // Prepare override for access token expiration
    const accessTokenExpirationOverride = getCookie(
      'ff-auth-token-max-seconds'
    );
    const accessTokenExpirationOverrideHeader: Record<string, string> =
      accessTokenExpirationOverride
        ? { 'x-auth-token-max-seconds': accessTokenExpirationOverride }
        : {};

    const params = new URLSearchParams(window.location.search);
    const testRefreshDelay = Number(params.get('test.auth.refreshDelay'));
    if (testRefreshDelay) {
      await sleep(testRefreshDelay * 1000);
    }

    if (params.has('test.auth.transientRefreshError')) {
      throw new TransientError('(test) transient refresh error');
    }

    if (params.has('test.auth.refreshError')) {
      throw new Error('(test) refresh error');
    }

    /**
     * Create the request to perform the refresh
     * Note: Must be compatible with builds for massdriver preview deployments
     * as well as the massdriver preview-header-extension
     *
     * Because this can be called from within a the Apollo link chain (AuthRefreshLink),
     * we construct a fetch request to the GraphQL API instead of using Apollo
     **/
    const {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      expiration: __newExpiration,
      sessionToken: newSessionToken,
    } = await fetchNewAccessToken({
      accessToken: this.getAccessToken(),
      clientName: CONFIG.thirdParty.apolloClient?.name,
      clientVersion: CONFIG.clientIdentifierVersion,
      headers: {
        ...session.getSessionHeaders(),
        ...accessTokenExpirationOverrideHeader,
      },
      refreshToken,
      sessionToken,
    });

    if (
      !newAccessToken ||
      (!newRefreshToken && !newSessionToken) ||
      !isValidAccessToken(newAccessToken)
    ) {
      throw new Error(
        'Request to refresh access token returned invalid response'
      );
    }

    return {
      access: newAccessToken,
      refresh: newRefreshToken,
      session: newSessionToken,
    };
  }
}

function delCookie(key: string) {
  removeCookie(key);
}

export default WindowAuth;

function getCookieStore(): EventTarget | undefined {
  if (!isMainThread) {
    return undefined;
  }
  return (window as any).cookieStore as EventTarget;
}
